const LEGACY_ASSET_BASE_URL = "https://sr360noc.com";
const getLegacyAssetUrl = (path) => `${LEGACY_ASSET_BASE_URL}${path}`;

// Central registry for forms that must bypass the generic/new document flow
// and use their legacy component, computations, and initials lifecycle.
// Add future special forms here as additional entries — nothing else in
// the app should hardcode form identifiers for this purpose again.

export const SPECIAL_FORMS = {
  energy: {
    key: "energy",
    frontendManaged: true,
    // Any of these values, found on either `form_id` or `name`, identify
    // this document as the Energy form.
    matchValues: ["energy"],
    loadComponent: () => import("./global/CreatingEnergyForm"),
    loadInitials: async () => {
      const { Initials } = await import("./global/initials/EnergyInitials");
      return Initials;
    },
    card: {
      form_id: "energy",
      name: "energy",
      label: "Energy Form R402",
      cover_img: getLegacyAssetUrl(
        "/images/permit_package/Global/energy-cover.jpg",
      ),
      img: [
        getLegacyAssetUrl("/images/permit_package/Global/energy_1.jpg"),
        getLegacyAssetUrl("/images/permit_package/Global/energy_2.jpg"),
        getLegacyAssetUrl("/images/permit_package/Global/energy_3.jpg"),
      ],
      pdf: "energy.pdf",
      signatures: {
        owner: { sign: true, notary: false },
        contractor: { sign: true, notary: false },
        order: 0,
      },
      need_noc: true,
      size: "letter",
      is_frontend_managed: true,
    },
  },
};

const SPECIAL_FORM_LIST = Object.values(SPECIAL_FORMS);

const normalize = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

/**
 * Resolves a document (or a bare form_id/name string) to its special-form
 * entry, checking both form_id and name so legacy-saved documents match
 * regardless of which field carries the identifier.
 */
export const getSpecialForm = (doc) => {
  if (!doc) return null;

  const candidates =
    typeof doc === "string"
      ? [doc]
      : [doc?.form_id, doc?.name, doc?.schema_type];

  const normalizedCandidates = candidates.map(normalize).filter(Boolean);
  if (!normalizedCandidates.length) return null;

  return (
    SPECIAL_FORM_LIST.find((form) =>
      form.matchValues.some((value) =>
        normalizedCandidates.includes(normalize(value)),
      ),
    ) || null
  );
};

export const isSpecialForm = (doc) => Boolean(getSpecialForm(doc));

export const getFrontendManagedSpecialFormCards = () =>
  SPECIAL_FORM_LIST.filter((form) => form.frontendManaged && form.card).map(
    (form) => ({ ...form.card }),
  );

/**
 * Separates database-backed form IDs from frontend-managed legacy forms.
 * The migrated package creation endpoint accepts only jurisdiction form IDs;
 * legacy forms are appended by canonical name after package creation.
 */
export const partitionSelectedFormsForCreation = (
  selectedFormIds = [],
  availableForms = [],
) => {
  const availableFormsById = new Map(
    availableForms
      .filter((form) => form?.form_id)
      .map((form) => [form.form_id, form]),
  );
  const apiFormIds = [];
  const legacyFormNames = [];

  selectedFormIds.filter(Boolean).forEach((formId) => {
    const availableForm = availableFormsById.get(formId);
    const specialForm = getSpecialForm(availableForm || formId);

    if (availableForm?.is_frontend_managed && specialForm?.frontendManaged) {
      legacyFormNames.push(specialForm.key);
      return;
    }

    apiFormIds.push(formId);
  });

  return {
    apiFormIds: [...new Set(apiFormIds)],
    legacyFormNames: [...new Set(legacyFormNames)],
  };
};

/**
 * Canonical key for a document — returns the special-form key (e.g.
 * "energy") when applicable, otherwise falls back to the normal
 * form_id/name resolution. Use this anywhere a document needs to be
 * counted, deduplicated, or looked up (availability lists, card counts,
 * selected-document matching).
 */
export const getCanonicalDocumentKey = (doc) => {
  const specialForm = getSpecialForm(doc);
  if (specialForm) return specialForm.key;

  return doc?.form_id || doc?.name || null;
};
